import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

interface Shopify {
  _id: string;
  webhookSigningKey?: string;
}

export function SettingsShopify() {
  const [shopify, setShopify] = useState<Shopify | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreatingShopify, setIsCreatingShopify] = useState(false);
  const [isDeletingShopify, setIsDeletingShopify] = useState(false);
  const [isSavingShopify, setIsSavingShopify] = useState(false);
  const [webhookSigningKey, setWebhookSigningKey] = useState("");

  useEffect(() => {
    async function fetchShopify() {
      setIsLoading(true);
      try {
        const shopifyResponse = await fetch("/api/shopify");
        if (shopifyResponse.ok) {
          const shopifyData = await shopifyResponse.json();
          if (shopifyData.shopify) {
            setShopify(shopifyData.shopify);
            setWebhookSigningKey(shopifyData.shopify.webhookSigningKey || "");
          }
        } else if (shopifyResponse.status !== 404) {
          console.error("Failed to fetch Shopify integration");
        }
      } catch (error) {
        console.error("Failed to fetch Shopify integration:", error);
      } finally {
        setIsLoading(false);
      }
    }

    fetchShopify();
  }, []);

  const handleCreateShopify = async () => {
    setIsCreatingShopify(true);
    try {
      const response = await fetch("/api/shopify", {
        method: "POST",
      });

      if (response.ok) {
        const data = await response.json();
        if (data.shopify) {
          setShopify(data.shopify);
          setWebhookSigningKey(data.shopify.webhookSigningKey || "");
          toast.success("Shopify integration created successfully");
        }
      } else {
        const error = await response.json();
        throw new Error(error.error || "Failed to create Shopify integration");
      }
    } catch (error) {
      console.error("Error creating Shopify integration:", error);
      toast.error(error instanceof Error ? error.message : "Failed to create Shopify integration");
    } finally {
      setIsCreatingShopify(false);
    }
  };

  const handleSaveShopify = async () => {
    if (!shopify) return;

    setIsSavingShopify(true);
    try {
      const response = await fetch("/api/shopify", {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ webhookSigningKey }),
      });

      if (response.ok) {
        const data = await response.json();
        if (data.shopify) {
          setShopify(data.shopify);
          toast.success("Shopify settings saved successfully");
        }
      } else {
        const error = await response.json();
        throw new Error(error.error || "Failed to save Shopify settings");
      }
    } catch (error) {
      console.error("Error saving Shopify settings:", error);
      toast.error(error instanceof Error ? error.message : "Failed to save Shopify settings");
    } finally {
      setIsSavingShopify(false);
    }
  };

  const handleDeleteShopify = async () => {
    if (!shopify) return;

    if (!confirm("Are you sure you want to delete your Shopify integration?")) {
      return;
    }

    setIsDeletingShopify(true);
    try {
      const response = await fetch(`/api/shopify/${shopify._id}`, {
        method: "DELETE",
      });

      if (response.ok) {
        setShopify(null);
        toast.success("Shopify integration deleted successfully");
      } else {
        const error = await response.json();
        throw new Error(error.error || "Failed to delete Shopify integration");
      }
    } catch (error) {
      console.error("Error deleting Shopify integration:", error);
      toast.error(error instanceof Error ? error.message : "Failed to delete Shopify integration");
    } finally {
      setIsDeletingShopify(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-[40vh]">
        <Loader2 className="animate-spin h-12 w-12 text-muted-foreground" />
      </div>
    );
  }

  const baseUrl = window.location.origin;

  return (
    <div className="max-w-2xl space-y-6">
      <div className="border rounded-lg p-4 space-y-4">
        <div className="flex items-center justify-between">
          <div className="space-y-1 flex-1">
            <h3 className="text-lg font-semibold">Shopify</h3>
            <p className="text-sm text-muted-foreground">
              Connect your Shopify store to sync products and orders
            </p>
            {shopify && (
              <div className="mt-2 space-y-1">
                <p className="text-sm text-green-600">Connected</p>
                <p className="text-sm text-muted-foreground">
                  Webhook URL: {baseUrl}/api/shopify/{shopify._id}
                </p>
              </div>
            )}
          </div>
          {!shopify ? (
            <Button onClick={handleCreateShopify} disabled={isCreatingShopify}>
              {isCreatingShopify ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating...
                </>
              ) : (
                "Create"
              )}
            </Button>
          ) : (
            <Button onClick={handleDeleteShopify} disabled={isDeletingShopify} variant="destructive">
              {isDeletingShopify ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                "Delete"
              )}
            </Button>
          )}
        </div>
        {shopify && (
          <div className="space-y-2 pt-4 border-t">
            <Label htmlFor="webhookSigningKey">Webhook Signing Key</Label>
            <Input
              id="webhookSigningKey"
              type="text"
              value={webhookSigningKey}
              onChange={(e) => setWebhookSigningKey(e.target.value)}
              placeholder="Enter Shopify webhook signing key"
            />
            <Button onClick={handleSaveShopify} disabled={isSavingShopify}>
              {isSavingShopify ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                "Save"
              )}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
